import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceMetadata } from '@/modules/Service/services'
import { ServiceInformationProps, ServiceStatus } from '@/modules/Service/types'
import Setting from '../Setting'
import { createDomainColumns, type DomainRow } from './domain-config'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation()
    const {
        data: metadata,
        renderState,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_METADATA, service.service_id],
        queryFn: () => getServiceMetadata(service.service_id),
    })

    // Create columns
    const columns = useMemo(() => {
        const cols = createDomainColumns(t)
        // Show/hide conditional columns
        return cols.map((col) => ({
            ...col,
            hidden:
                col.key === 'service_stopped_date'
                    ? !service?.service_stopped_date
                    : col.key === 'service_renew_cycling'
                      ? service?.service_status !== ServiceStatus.Active
                      : col.hidden,
        }))
    }, [t, service?.service_stopped_date, service?.service_status])

    // Create row data
    const rows = useMemo<DomainRow[]>(
        () => [
            {
                ...service,
                domain_name: metadata?.metadata?.domain_value || service.name,
            },
        ],
        [service, metadata]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            <DataTableCustomized<DomainRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => String(row?.service_id)}
                size="small"
                resizable
                emptyMessage={t('no-data', { defaultValue: 'No data' })}
            />

            {metadata?.metadata?.domain_extra_fields && (
                <Setting
                    service={service}
                    metadata={metadata}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default Information
